import React, { useState, useRef, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Sparkles, Send, Loader2, Wand2 } from 'lucide-react';
import api from '../api/client';

const SUGGESTIONS = [
  "Résume mon activité de la semaine",
  "Génère un programme de formation sur Python",
  "Combien de apprenants actifs j'ai ?",
  "Quels sont mes 3 critères Qualiopi les plus faibles ?",
];

export default function PetronilleModal({ open, onOpenChange }) {
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Bonjour 👋 Je suis OFia, votre assistante IA OFManager. Comment puis-je vous aider aujourd'hui ?" },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post('/ai/chat', { session_id: sessionId, message: msg });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: 'assistant', text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', text: "Désolée, je rencontre un problème. Réessayez dans un instant." }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSummary = async () => {
    setLoading(true);
    setMessages((m) => [...m, { role: 'user', text: 'Résume mon activité de la semaine' }]);
    try {
      const { data } = await api.get('/ai/weekly-summary');
      setMessages((m) => [...m, { role: 'assistant', text: data.summary }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', text: "Je n'ai pas pu générer le résumé." }]);
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[580px] p-0 rounded-2xl overflow-hidden flex flex-col" style={{ height: '620px' }}>
        <DialogHeader className="p-5 text-white shrink-0" style={{ background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <DialogTitle className="text-white text-[18px]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>OFia · Assistante IA</DialogTitle>
              <div className="text-[12px] text-white/80 flex items-center gap-1.5"><span className="pulse-dot" style={{ background: '#BEFBE6' }} /> En ligne · GPT-4o-mini</div>
            </div>
          </div>
        </DialogHeader>

        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-3 bg-[#FAFAFE]">
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[82%] px-3.5 py-2.5 rounded-2xl text-[13.5px] leading-relaxed ${
                m.role === 'user' ? 'brand-gradient text-white rounded-br-sm' : 'bg-white border border-[#EFF0F6] text-[#1A1B2E] rounded-bl-sm'
              }`}>
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex justify-start">
              <div className="bg-white border border-[#EFF0F6] rounded-2xl rounded-bl-sm px-3.5 py-2.5 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin text-[#4F46E5]" />
                <span className="text-[12.5px] text-[#8E8FA5]">OFia réfléchit…</span>
              </div>
            </div>
          )}
          {messages.length <= 1 && !loading && (
            <div className="pt-4 space-y-2">
              <div className="text-[11.5px] font-semibold uppercase tracking-wider text-[#8E8FA5] px-1">Idées pour commencer</div>
              {SUGGESTIONS.map((s) => (
                <button key={s} onClick={() => send(s)} className="w-full text-left text-[13px] p-3 rounded-xl bg-white border border-[#EFF0F6] hover:border-[#C7D2FE] hover:bg-[#EEF2FF] transition-colors flex items-center gap-2">
                  <Wand2 className="w-3.5 h-3.5 text-[#4F46E5]" /> {s}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="shrink-0 border-t border-[#EFF0F6] p-3 bg-white">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={handleSummary} disabled={loading} className="text-[11.5px] font-semibold px-2 py-1 rounded-lg bg-[#EEF2FF] text-[#4F46E5] hover:bg-[#EDE8FF] disabled:opacity-50">✨ Résumé semaine</button>
            <span className="text-[11px] text-[#8E8FA5]">Propulsé par Emergent LLM</span>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Posez une question à OFia…"
              className="flex-1 h-10 px-3.5 rounded-xl bg-[#F6F7FB] border border-[#EFF0F6] text-[13.5px] focus:bg-white focus:border-[#4F46E5] focus:ring-2 focus:ring-[#EDE8FF] outline-none transition-colors"
              disabled={loading}
            />
            <button type="submit" disabled={loading || !input.trim()} className="h-10 w-10 rounded-xl brand-gradient flex items-center justify-center shadow-sm disabled:opacity-50">
              {loading ? <Loader2 className="w-4 h-4 text-white animate-spin" /> : <Send className="w-4 h-4 text-white" />}
            </button>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
